import React, { useState, useEffect, useMemo } from "react";
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Label,
} from "@headlessui/react";
import { ChevronUpDownIcon } from "@heroicons/react/20/solid";
import {
  format24to12,
  format12To24,
  parseTime,
} from "../../config/availability";
import { nanoid } from "nanoid";
import { DateTime } from "luxon";
import { useUserPreferences } from "../../hooks/useUserPreferences";
import useIsMobile from "../../hooks/useIsMobile";

const TimeSelector = ({
  label,
  onSelect,
  is12HourClock,
  value,
  timeDataFrom,
  isError,
}) => {
  const { is12HourClock: userPrefers12Hour } = useUserPreferences();
  const actualIs12HourClock =
    is12HourClock !== undefined ? is12HourClock : userPrefers12Hour;
  const isMobile = useIsMobile();

  // On mobile, make the field a tap-to-open dropdown instead of a text input:
  // readOnly + inputMode:none suppress the software keyboard (same trick as
  // SearchLookup's mobile trigger). Free-typing a custom time stays available
  // on desktop. Neither consumer (TimeOffForm, WeeklyAvailability) relies on
  // typed custom times, so this only changes the mobile input affordance.
  const mobileKeyboardProps = isMobile
    ? { readOnly: true, inputMode: "none" }
    : {};

  const [selectedOption, setSelectedOption] = useState(null);
  const [query, setQuery] = useState("");
  const [comboBoxData, setComboBoxData] = useState([]);
  const timeSelectorData = useMemo(() => {
    const times = [];

    const formatTime = (hour, minute) => {
      const time = DateTime.now().set({ hour, minute });
      return actualIs12HourClock
        ? time.toFormat("hh:mm a")
        : time.toFormat("HH:mm");
    };

    let timeFrom;
    if (timeDataFrom) {
      timeFrom = parseTime(timeDataFrom);
    }

    let startHour = timeFrom?.hour ?? 0;
    let startMinute = timeFrom?.minute ?? 0;

    for (let hour = startHour; hour < 24; hour++) {
      for (let minute = startMinute; minute < 60; minute += 15) {
        const formattedTime = formatTime(hour, minute);
        times.push({
          id: nanoid(),
          name: formattedTime,
        });
      }
      startMinute = 0; // Reset minutes after the first hour
    }

    return times;
  }, [actualIs12HourClock, timeDataFrom]);

  useEffect(() => {
    if (!selectedOption) return;

    let filteredData = query
      ? timeSelectorData.filter((result) =>
          result.name.includes(query.toLowerCase())
        )
      : timeSelectorData;

    filteredData = filteredData.map((timeData) =>
      timeData.name === selectedOption.name
        ? { ...timeData, id: selectedOption.id }
        : timeData
    );

    setComboBoxData(filteredData);
  }, [timeSelectorData, query, selectedOption]);

  useEffect(() => {
    const formatTime = (time) => {
      let formattedTime;

      if (actualIs12HourClock) {
        formattedTime = format24to12(time);
      } else {
        formattedTime = time;
      }

      return {
        id: nanoid(),
        name: formattedTime,
      };
    };

    if (value && !selectedOption) {
      const formattedValue = formatTime(value.name);
      setSelectedOption(formattedValue);
    }
  }, [value, selectedOption, actualIs12HourClock]);

  const validateCustomTime = (time) => {
    const date = actualIs12HourClock
      ? DateTime.fromFormat(`1970-01-01 ${time}`, "yyyy-MM-dd hh:mm a")
      : DateTime.fromFormat(`1970-01-01 ${time}`, "yyyy-MM-dd HH:mm");
    const newTime = actualIs12HourClock
      ? date.toFormat("hh:mm a")
      : date.toFormat("HH:mm");

    const data = {
      id: nanoid(),
      name: newTime,
    };
    if (date.isValid) {
      onSelect({
        id: data.id,
        name: actualIs12HourClock ? format12To24(data.name) : data.name,
      });
      setSelectedOption(data);
    } else {
      setQuery("");
    }
  };
  return (
    <Combobox
      as="div"
      value={selectedOption}
      onChange={(data) => {
        if (!data) return;
        onSelect({
          id: data.id,
          name: actualIs12HourClock ? format12To24(data.name) : data.name,
        });
        setSelectedOption(data);
      }}
    >
      <Label className={`block text-sm font-medium text-ink-900`}>
        {label}
      </Label>
      <div className="relative mt-2">
        <ComboboxInput
          className={`block w-full truncate rounded-md bg-white py-1.5 pl-2 text-base text-ink-900 outline outline-1 -outline-offset-1 sm:text-sm/6 ${
            !isError
              ? "outline-ink-200 focus:outline-accent-500"
              : "outline-signal-red-500 focus:outline-signal-red-500"
          }  placeholder:text-ink-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-accent-500`}
          onChange={(event) => setQuery(event.target.value)}
          onBlur={() => {
            validateCustomTime(query);
            setQuery("");
          }}
          displayValue={(data) => (data ? data.name : "")}
          {...mobileKeyboardProps}
        />
        {/* On mobile the input is readOnly (no keyboard), so a full-field
            ComboboxButton overlay catches taps and opens the dropdown. Desktop
            keeps only the right-edge chevron below so the input stays typeable. */}
        {isMobile && (
          <ComboboxButton
            aria-label="Choose a time"
            className="absolute inset-0 h-full w-full cursor-pointer rounded-md focus:outline-none"
          />
        )}
        <ComboboxButton className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
          <ChevronUpDownIcon
            className="size-5 text-ink-400"
            aria-hidden="true"
          />
        </ComboboxButton>

        {comboBoxData.length > 0 && (
          <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none">
            {comboBoxData.map((data) => (
              <ComboboxOption
                key={data.id}
                value={data}
                className="group relative cursor-default select-none py-2 pl-3 pr-3 text-ink-900 data-[focus]:bg-accent-500 data-[focus]:text-white data-[focus]:outline-none"
              >
                <div className="flex items-center">
                  <span className="truncate w-full">{data.name}</span>
                </div>
              </ComboboxOption>
            ))}
          </ComboboxOptions>
        )}
      </div>
    </Combobox>
  );
};

export default TimeSelector;
